import React, { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import Image from "next/legacy/image";
import Link from "@/utils/link";

import { ChevronRightIcon, ChevronDownIcon } from "@heroicons/react/outline";

import { getMDXComponent } from "mdx-bundler/client";

import { processMdx } from "@/utils/mdx";
import HomeHeader from "@/components/home/HomeHeader";
import { fetchGraphql } from "@/utils/query";
import { imageLoader, removeExt } from "@/utils/image";

import Breadcrumbs from "@/components/common/Breadcrumbs";
import SeoLinks from "@/components/home/SeoLinks";
import HomeFooter from "@/components/home/HomeFooter";
import Button from "@/ui/Button";

import NotFound from "@/components/common/NotFound";
import { isProspect } from "@/utils/type";

const CustomLink = (props) => {
    const href = props.href;
    const isInternalLink = href && (href.startsWith("/") || href.startsWith("#"));

    if (isInternalLink) {
        return (
            <Link href={href}>
                <a {...props}>{props.children}</a>
            </Link>
        );
    }

    return <a target="_blank" rel="noopener noreferrer" {...props} />;
};

const MDXComponents = {
    Image,
    a: CustomLink,
};

const Help = ({ title, sections, type }) => {
    const router = useRouter();
    const [openAll, setOpenAll] = useState(0);
    const [closeAll, setCloseAll] = useState(0);

    if (!isProspect(type)) {
        return <NotFound />;
    }

    return (
        <div className="h-screen bg-gray-50">
            <HomeHeader fixed />

            <div className="container mx-auto pt-16 sm:pt-20 pb-4 px-4 sm:px-16">
                <Breadcrumbs links={[{ name: "Help", href: "/help" }]} />
            </div>

            <div>
                <div className="hidden sm:block">
                    <div className="relative w-full h-40 sm:h-72 bg-black">
                        <Image
                            loader={imageLoader}
                            src="https://cdn.purpledoorfinders.com/purpledoorfinders/purpledoorfinders-help"
                            alt="purple-door-finders-help"
                            layout="fill"
                            objectFit="cover"
                            objectPosition="0px -200px"
                            priority
                            unoptimized={false}
                            className="opacity-80"
                        />

                        <div className="container mx-auto pt-16 sm:pt-20 pb-4 px-4 sm:px-16">
                            <h1 className="absolute top-12 sm:top-32 text-3xl sm:text-5xl text-white font-headline font-bold">
                                Help
                            </h1>
                        </div>
                    </div>
                </div>

                <div className="sm:hidden">
                    <h1 className="px-4 text-3xl text-primary-500 font-headline font-bold">
                        Help
                    </h1>
                </div>
            </div>

            <div className="pt-8 pb-8 container mx-auto px-4 sm:px-16">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-16">
                    <div className="md:col-span-2 space-y-2">
                        <div className="mb-4 flex space-x-4">
                            <Button
                                color="gray"
                                size="xs"
                                variant="outlined"
                                onClick={() => {
                                    setOpenAll((x) => x + 1);
                                    setCloseAll(0);
                                }}
                            >
                                Open All
                            </Button>
                            <Button
                                color="gray"
                                size="xs"
                                variant="outlined"
                                onClick={() => {
                                    setOpenAll(0);
                                    setCloseAll((x) => x + 1);
                                }}
                            >
                                Close All
                            </Button>
                        </div>

                        {sections.map((section, index) => (
                            <Section
                                key={index}
                                section={section}
                                openAll={openAll}
                                closeAll={closeAll}
                            />
                        ))}
                    </div>

                    <div className="mt-8 sm:mt-0">
                        <h3 className="text-xl text-primary-500 font-headline font-bold">
                            Additional Resources
                        </h3>

                        <div className="mt-4 w-full flex flex-col space-y-4 divide-y-2">
                            <SideLink href="/resources">
                                Assisted Living Assessment
                            </SideLink>

                            <SideLink href="/resources/financial-calculator">
                                Assisted Living Financial Calculator
                            </SideLink>

                            <SideLink href="/resources/timeline">
                                Assisted Living Timeline
                            </SideLink>

                            <SideLink href="/resources/guidebooks/Guide-for-Affording-Senior-Living-and-Assisted-Living">
                                Guidebook for Affording Senior Living
                            </SideLink>

                            <SideLink href="/resources/guidebooks/guide-to-assisted-living">
                                Guidebook for Assisted Living
                            </SideLink>

                            <SideLink href="/resources/guidebooks/guide-to-memory-care">
                                Guidebook for Memory Care
                            </SideLink>
                        </div>

                        <div className="mt-16">
                            <h3 className="text-xl text-primary-500 font-headline font-bold">
                                Have More Questions?
                            </h3>

                            <p className="mt-2">Visit our FAQ pages.</p>

                            <Button
                                variant="contained"
                                href="/resources/faq"
                                className="mt-4 w-32"
                            >
                                See FAQ
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="z-10">
                <SeoLinks />
                <HomeFooter />
            </div>
        </div>
    );
};

const Section = ({ section, openAll, closeAll }) => {
    const [open, setOpen] = useState(openAll > 0);

    console.log("all", open, openAll, closeAll);

    useEffect(() => {
        if (openAll > 0) {
            setOpen(true);
        } else if (closeAll > 0) {
            setOpen(false);
        }
    }, [openAll, closeAll]);

    const { code } = section;
    const Component = useMemo(() => getMDXComponent(code), [code]);

    return (
        <div>
            <button
                onClick={() => setOpen((x) => !x)}
                className="w-full flex items-center pl-1 pr-4 py-2 text-left text-primary-500 bg-gray-300 hover:bg-gray-200"
            >
                <div className="h-6">
                    {open ? (
                        <ChevronDownIcon className="w-6 h-6 mr-2" />
                    ) : (
                        <ChevronRightIcon className="w-6 h-6 mr-2" />
                    )}
                </div>
                <span className="font-headline font-bold text-lg">{section.heading}</span>
            </button>

            {open ? (
                <div className="pl-8 pr-4 mb-8 prose max-w-3xl">
                    <Component components={MDXComponents} />
                </div>
            ) : null}
        </div>
    );
};

const SideLink = ({ children, href }) => {
    return (
        <Link href={href}>
            <a className="w-full pt-2 text-md font-bold hover:underline">{children}</a>
        </Link>
    );
};

export const getServerSideProps = async () => {
    const result = await fetchGraphql(
        `
        query($find: JSON) {
            pages(find: $find) {
                pageId
                title
                secondaryHeading
                content                
            }
        }
    `,
        {
            find: {
                pageId: "help",
            },
        }
    );

    let page = null;
    if (result && result.data && result.data.pages && result.data.pages.length > 0) {
        page = result.data.pages[0];
        console.log("page", page);
    } else {
        return {
            props: {
                title: "help",
                sections: [],
            },
        };
    }

    let sections = [];
    if (page.content) {
        const f = page.content.split("##");

        for (let i = 1; i < f.length; i++) {
            const lines = f[i].split("\n");
            const heading = lines[0];
            const content = lines.filter((_, index) => index > 0).join("\n");
            const post = await processMdx(
                content.replaceAll("\n", "  \n").replaceAll("<br>", "  \n")
            );
            sections.push({ heading, ...post });
        }
    }

    return {
        props: {
            title: page.title,
            sections,
            type: process.env.NEXT_PUBLIC_TYPE,
        },
    };
};

export default Help;
